import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import {Button, SelectPanel} from '@primer/react'
import {type ComponentProps, useCallback, useMemo, useState} from 'react'

import {MemexWorkflowContentType} from '../../api/workflows/contracts'
import {assertNever} from '../../helpers/assert-never'
import {useAutomationGraph} from '../../state-providers/workflows/use-automation-graph'
import {Resources, WorkflowResources} from '../../strings'
import {buildPickerLabel} from './helpers/accessible-label'

function getDisplayTextFromMemexWorkflowContentType(value: MemexWorkflowContentType) {
  switch (value) {
    case MemexWorkflowContentType.PullRequest: {
      return Resources.pullRequest
    }
    case MemexWorkflowContentType.Issue: {
      return Resources.issue
    }
    default: {
      assertNever(value)
    }
  }
}

type ContentTypePickerProps = ComponentProps<'div'> & {
  onContentTypesSelected: (contentTypes: Array<MemexWorkflowContentType>) => void
  contentTypes: Array<MemexWorkflowContentType>
  isEditing?: boolean
  selectedContentTypes: Array<MemexWorkflowContentType>
  testId: string
  headerDescription?: string
}

export const ContentTypePicker: React.FC<ContentTypePickerProps> = ({
  selectedContentTypes,
  contentTypes,
  isEditing,
  onContentTypesSelected,
  testId,
  headerDescription,
}) => {
  const {setIsWorkflowValid} = useAutomationGraph()

  const items = useMemo(
    () =>
      contentTypes.map((type: MemexWorkflowContentType) => ({
        text: getDisplayTextFromMemexWorkflowContentType(type),
        id: type,
      })),
    [contentTypes],
  )

  const [filter, setFilter] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const validate = useCallback(
    (selectedItems: Array<MemexWorkflowContentType>) => {
      if (selectedItems.length === 0) {
        setIsWorkflowValid(false)
      } else {
        setIsWorkflowValid(true)
      }
    },
    [setIsWorkflowValid],
  )

  const onSelectedChange = (selectedItems: Array<ItemInput>) => {
    const mappedItems = selectedItems.map(item => item.id)
    const filteredItems = mappedItems.filter(
      (item): item is MemexWorkflowContentType => item !== undefined && item in MemexWorkflowContentType,
    )
    validate(filteredItems)
    onContentTypesSelected(filteredItems)
  }

  const filteredItems = items.filter(type => type.text.toLowerCase().startsWith(filter.toLowerCase()))
  const selected = items.filter(item => selectedContentTypes.includes(item.id))
  // Build the visible + accessible label:
  const selectedText = selected.map(s => s.text).join(', ')
  const label = buildPickerLabel(headerDescription, selectedText)
  return (
    <SelectPanel
      placeholderText={WorkflowResources.selectContentType}
      open={isOpen}
      onOpenChange={setIsOpen}
      selected={selected}
      onFilterChange={setFilter}
      items={filteredItems}
      onSelectedChange={onSelectedChange}
      renderAnchor={({children, ...anchorProps}) => (
        <Button
          disabled={!selected || !isEditing}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          aria-label={label}
          {...testIdProps(`${testId}-anchor`)}
        >
          {selected.length > 0 ? children : WorkflowResources.invalidNoContentType}
        </Button>
      )}
      overlayProps={{
        width: 'small',
        onMouseDown: e => e.stopPropagation(),
        height: 'auto',
        onClickOutside: onClose,
        ...testIdProps(`${testId}-panel`),
      }}
    />
  )
}
